import type { KeyboardEvent, MouseEvent, ReactNode } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface VersionRowBadgeProps {
  /** The glyph drawn in the row; the popover is what it opens. */
  icon: ReactNode;
  /** Accessible name of the trigger — it is the only text a screen reader gets before opening. */
  ariaLabel: string;
  testId?: string;
  /** Test hook on the popover body; defaults to `<testId>-popover`. */
  popoverTestId?: string;
  /** The popover body. */
  children?: ReactNode;
  /** Extra classes on the trigger button. */
  className?: string;
}

/**
 * A mark on a version row that opens a popover, without the row taking the activation.
 *
 * A version row is a `role="button"` that selects the version, so every way of reaching the
 * badge has to stop there: pointer, keyboard and touch. Hover opens it on a short delay so a
 * pointer crossing the row does not flash a panel; click and Enter/Space open it for everyone
 * who does not hover.
 */
export function VersionRowBadge({
  icon,
  ariaLabel,
  testId,
  popoverTestId,
  children,
  className,
}: VersionRowBadgeProps) {
  const swallow = (event: MouseEvent) => {
    event.stopPropagation();
  };

  /**
   * Keep Enter and Space from reaching the row.
   *
   * The row's key handler `preventDefault()`s both — which, on a focused button, is exactly
   * what stops the browser from synthesising the `click` that opens the popover. So a keyboard
   * user pressing Enter on the badge would select the version behind it and never see the
   * popover at all. This runs on the portaled content too: a React portal still bubbles events
   * up the React tree, so a button inside the popover reaches the row handler even though it is
   * not inside the row in the DOM.
   *
   * Only the activation keys are stopped. Arrow keys keep bubbling on purpose, so a keyboard
   * user can still walk the version list from here.
   */
  const swallowActivationKeys = (event: KeyboardEvent) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.stopPropagation();
  };

  return (
    <Popover>
      <PopoverTrigger
        openOnHover
        delay={150}
        closeDelay={120}
        render={
          <button
            type="button"
            className={cn(
              "ml-1.5 inline-flex items-center align-middle transition-opacity hover:opacity-80",
              className
            )}
            onClick={swallow}
            onKeyDown={swallowActivationKeys}
            aria-label={ariaLabel}
            data-testid={testId}
          />
        }
      >
        {icon}
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="w-64 gap-2 p-3"
        onClick={swallow}
        onKeyDown={swallowActivationKeys}
        data-testid={popoverTestId ?? (testId ? `${testId}-popover` : undefined)}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}
